import type { ItemId } from '../inventory';

/** A weighted entry in a world's deposit pool. Higher weight = more
 *  frequent. The roller normalises these so absolute values don't matter,
 *  only their ratio. */
export interface WorldResourceEntry {
  readonly itemId: ItemId;
  readonly weight: number;
  /** Per-deposit unit yield range. The miner rolls within these bounds
   *  when a deposit is destroyed. */
  readonly yieldMin: number;
  readonly yieldMax: number;
}

export interface WorldResourcePool {
  readonly displayName: string;
  /** Roughly how many mineable deposits the surface should spawn. The
   *  scene clamps this against its own geometry budget. */
  readonly depositCount: number;
  readonly entries: readonly WorldResourceEntry[];
  /** Short flavour line for the planet — used in HUD/log if needed. */
  readonly tagline: string;
}

const POOLS: Record<string, WorldResourcePool> = {
  Caelis: {
    displayName: 'Caelis',
    depositCount: 22,
    tagline: 'Arid salt flats and pale dunes.',
    entries: [
      { itemId: 'sodium', weight: 6, yieldMin: 2, yieldMax: 4 },
      { itemId: 'raw-quartz', weight: 4, yieldMin: 1, yieldMax: 3 },
      { itemId: 'hydrogen', weight: 1, yieldMin: 1, yieldMax: 2 }
    ]
  },
  Verdia: {
    displayName: 'Verdia',
    depositCount: 28,
    tagline: 'Biological forests and pulsing crust.',
    entries: [
      { itemId: 'living-rock', weight: 2, yieldMin: 1, yieldMax: 2 },
      { itemId: 'raw-quartz', weight: 5, yieldMin: 2, yieldMax: 3 },
      { itemId: 'hydrogen', weight: 4, yieldMin: 2, yieldMax: 3 },
      { itemId: 'sodium', weight: 2, yieldMin: 1, yieldMax: 2 }
    ]
  },
  'Verdia Minor': {
    displayName: 'Verdia Minor',
    depositCount: 18,
    tagline: 'Stripped moon — exposed metal seams.',
    entries: [
      { itemId: 'silver', weight: 4, yieldMin: 1, yieldMax: 3 },
      { itemId: 'stellar-iron', weight: 3, yieldMin: 1, yieldMax: 2 },
      { itemId: 'raw-quartz', weight: 3, yieldMin: 1, yieldMax: 2 }
    ]
  },
  Ambre: {
    displayName: 'Ambre',
    depositCount: 24,
    tagline: 'Iron desert under amber rings.',
    entries: [
      { itemId: 'stellar-iron', weight: 4, yieldMin: 2, yieldMax: 3 },
      { itemId: 'raw-quartz', weight: 5, yieldMin: 2, yieldMax: 3 },
      { itemId: 'sodium', weight: 3, yieldMin: 1, yieldMax: 2 },
      { itemId: 'amber-core', weight: 1, yieldMin: 1, yieldMax: 1 }
    ]
  },
  Auron: {
    displayName: 'Auron',
    depositCount: 24,
    tagline: 'Golden glass plains over a hot core.',
    entries: [
      { itemId: 'amber-core', weight: 2, yieldMin: 1, yieldMax: 2 },
      { itemId: 'silver', weight: 3, yieldMin: 1, yieldMax: 3 },
      { itemId: 'stellar-iron', weight: 3, yieldMin: 1, yieldMax: 2 },
      { itemId: 'sodium', weight: 2, yieldMin: 1, yieldMax: 2 }
    ]
  },
  Solenne: {
    displayName: 'Solenne',
    depositCount: 26,
    tagline: 'Crystal storms above a frozen sea.',
    entries: [
      { itemId: 'aurora-crystal', weight: 2, yieldMin: 1, yieldMax: 2 },
      { itemId: 'plasma-crystal', weight: 1, yieldMin: 1, yieldMax: 1 },
      { itemId: 'hydrogen', weight: 4, yieldMin: 2, yieldMax: 4 },
      { itemId: 'raw-quartz', weight: 3, yieldMin: 1, yieldMax: 2 }
    ]
  },
  'Solenne Echo': {
    displayName: 'Solenne Echo',
    depositCount: 16,
    tagline: 'Tidally locked moon glittering with photophores.',
    entries: [
      { itemId: 'lumen-dust', weight: 1, yieldMin: 1, yieldMax: 1 },
      { itemId: 'aurora-crystal', weight: 2, yieldMin: 1, yieldMax: 2 },
      { itemId: 'silver', weight: 3, yieldMin: 1, yieldMax: 2 }
    ]
  },
  Nyara: {
    displayName: 'Nyara',
    depositCount: 22,
    tagline: 'Frozen highlands wrapped in storms.',
    entries: [
      { itemId: 'hydrogen', weight: 5, yieldMin: 2, yieldMax: 4 },
      { itemId: 'ion-ice', weight: 1, yieldMin: 1, yieldMax: 1 },
      { itemId: 'raw-quartz', weight: 4, yieldMin: 1, yieldMax: 3 },
      { itemId: 'sodium', weight: 2, yieldMin: 1, yieldMax: 2 }
    ]
  },
  'Nyara Whisper': {
    displayName: 'Nyara Whisper',
    depositCount: 14,
    tagline: 'Radioactive shards in a quiet shadow.',
    entries: [
      { itemId: 'uranium-ore', weight: 2, yieldMin: 1, yieldMax: 2 },
      { itemId: 'raw-quartz', weight: 4, yieldMin: 1, yieldMax: 2 },
      { itemId: 'hydrogen', weight: 2, yieldMin: 1, yieldMax: 2 }
    ]
  }
};

/** Returns the deposit pool for a world by display name, or null if it
 *  isn't a registered surface (e.g. an unmapped procedural body). */
export function getWorldResourcePool(displayName: string): WorldResourcePool | null {
  return POOLS[displayName] ?? null;
}

/** Roll a weighted random entry from the pool. Returns null if the pool
 *  has no entries (defensive — shouldn't happen for our hand-authored
 *  worlds, but keeps the caller safe). */
export function rollResourceEntry(
  pool: WorldResourcePool,
  rng: () => number = Math.random
): WorldResourceEntry | null {
  if (pool.entries.length === 0) return null;
  let total = 0;
  for (const entry of pool.entries) total += entry.weight;
  if (total <= 0) return null;
  let roll = rng() * total;
  for (const entry of pool.entries) {
    roll -= entry.weight;
    if (roll <= 0) return entry;
  }
  return pool.entries[pool.entries.length - 1] ?? null;
}

/** Roll the actual yield amount for a destroyed deposit, integer in
 *  [yieldMin, yieldMax]. */
export function rollYield(
  entry: WorldResourceEntry,
  rng: () => number = Math.random
): number {
  const range = entry.yieldMax - entry.yieldMin;
  return entry.yieldMin + Math.floor(rng() * (range + 1));
}
